import { flat } from '@/lib/search'
import type { IconName } from '@/ui/icons'

/**
 * Navigation: every screen of the app, its permission and its icon. The label
 * and subtitle are translations: `nav.<key>.label` and `nav.<key>.sub`. The
 * same permission guards the route (router.ts) and the menu entry.
 */
/** `technical`: a screen for the platform's technical team, routed and searchable but out of the sidebar. */
export interface NavItem { key: string; to: string; icon: IconName; perm: string; technical?: true }
export interface NavGroup { key: string; items: NavItem[] }

/*
 * The sidebar by trade (2026-10-22, chosen by the user on the menu's mock-up): eight groups instead of five,
 * « Configuration » no longer holding fourteen screens of every kind. Order matters: an account lands on its
 * first screen (landingFor), an admin on Direction.
 */
export const NAV: NavGroup[] = [
  { key: 'operations', items: [
    { key: 'agent', to: '/agent', icon: 'phone', perm: 'agent.work' },
    { key: 'direction', to: '/direction', icon: 'trending-up', perm: 'report.view' },
    { key: 'supervision', to: '/supervision', icon: 'activity', perm: 'monitor.view' },
    { key: 'dashboard', to: '/dashboard', icon: 'layout-dashboard', perm: 'report.view' },
    { key: 'leaderboard', to: '/leaderboard', icon: 'star', perm: 'leaderboard.view' },
  ] },
  { key: 'campaigns', items: [
    { key: 'campaigns', to: '/campaigns', icon: 'megaphone', perm: 'campaign.read' },
    { key: 'contacts', to: '/contacts', icon: 'list', perm: 'contact.read' },
    { key: 'callbacks', to: '/callbacks', icon: 'clock', perm: 'callback.manage' },
    { key: 'callLog', to: '/call-log', icon: 'phone-forwarded', perm: 'call.log.view' },
    { key: 'recycling', to: '/recycling', icon: 'refresh', perm: 'recycle.view' },
    { key: 'scripts', to: '/scripts', icon: 'edit', perm: 'script.view' },
    { key: 'dispositions', to: '/dispositions', icon: 'tag', perm: 'disposition.view' },
  ] },
  { key: 'sales', items: [
    { key: 'outlets', to: '/outlets', icon: 'store', perm: 'outlet.view' },
    { key: 'orders', to: '/orders', icon: 'shopping-cart', perm: 'order.view' },
    { key: 'catalog', to: '/catalog', icon: 'package', perm: 'catalog.view' },
    { key: 'territories', to: '/territories', icon: 'map-pin', perm: 'territory.view' },
  ] },
  { key: 'messages', items: [
    { key: 'whatsapp', to: '/whatsapp', icon: 'message-circle', perm: 'whatsapp.campaigns' },
    { key: 'sms', to: '/sms', icon: 'send', perm: 'sms.view' },
  ] },
  { key: 'team', items: [
    { key: 'users', to: '/users', icon: 'users', perm: 'user.read' },
    { key: 'roles', to: '/roles', icon: 'shield', perm: 'role.manage' },
    { key: 'breaks', to: '/breaks', icon: 'coffee', perm: 'pause.manage' },
    { key: 'quality', to: '/quality', icon: 'shield-check', perm: 'quality.view' },
    { key: 'coaching', to: '/coaching', icon: 'graduation-cap', perm: 'coaching.view' },
    { key: 'wfm', to: '/wfm', icon: 'calendar', perm: 'wfm.view' },
  ] },
  { key: 'analysis', items: [
    { key: 'reports', to: '/reports', icon: 'bar-chart', perm: 'report.view' },
    // AI step 2: questions about the figures, answered from the reports (module ai).
    { key: 'assistant', to: '/assistant', icon: 'sparkles', perm: 'ai.report' },
    { key: 'funnel', to: '/system/funnel', icon: 'trending-down', perm: 'monitor.view' },
  ] },
  { key: 'telephony', items: [
    { key: 'gsm', to: '/gsm-lines', icon: 'phone-outgoing', perm: 'gsm.view' },
    { key: 'inbound', to: '/inbound', icon: 'phone-incoming', perm: 'inbound.view' },
  ] },
  { key: 'administration', items: [
    { key: 'settings', to: '/settings', icon: 'settings', perm: 'settings.read' },
    { key: 'importExport', to: '/import-export', icon: 'upload', perm: 'list.import' },
    { key: 'fields', to: '/fields', icon: 'columns', perm: 'sales.config' },
    { key: 'picklists', to: '/picklists', icon: 'filter', perm: 'sales.config' },
    // The company's API keys and the API's documentation (« Marketplace & API », 2026-10-12).
    { key: 'integrations', to: '/integrations', icon: 'command', perm: 'api.manage' },
    { key: 'billing', to: '/billing', icon: 'credit-card', perm: 'billing.view' },
    { key: 'audit', to: '/audit', icon: 'file-text', perm: 'audit.read' },
    // A person's data, exported or erased on request (review R10).
    { key: 'privacy', to: '/privacy', icon: 'shield', perm: 'privacy.manage' },
    { key: 'health', to: '/system', icon: 'globe', perm: 'system.health.view' },
    { key: 'incidents', to: '/system/incidents', icon: 'alert-circle', perm: 'system.diagnostics.view', technical: true },
    { key: 'callTimeline', to: '/system/call-timeline', icon: 'clock', perm: 'system.diagnostics.view', technical: true },
    { key: 'designSystem', to: '/design-system', icon: 'palette', perm: 'settings.read', technical: true },
  ] },
]

export const NAV_ITEMS: NavItem[] = NAV.flatMap((g) => g.items)

/**
 * A role's own sidebar (2026-10-22, API `role_menus`): its sections in order with their screens, and the
 * screens it hides, as the company's admins drag them on « Rôles et permissions ». Keys of NAV only.
 */
export interface MenuLayout { groups: Array<{ key: string; items: string[] }>; hidden: string[] }

/**
 * The menu as a role's layout arranges it: its sections and screens in its order, the hidden ones left out.
 * What the layout does not know is ignored (a removed screen); a screen it does not name (a new one) goes at
 * the end of its default section, so no screen is ever lost. Never more than NAV: permissions still filter.
 */
export function arrangeNav(layout: MenuLayout | null | undefined): NavGroup[] {
  if (!layout) return NAV
  const byKey = new Map(NAV_ITEMS.map((i) => [i.key, i]))
  const placed = new Set(layout.hidden.filter((k) => byKey.has(k)))
  const groups: NavGroup[] = []
  for (const g of layout.groups) {
    if (!NAV.some((d) => d.key === g.key) || groups.some((x) => x.key === g.key)) continue
    const items: NavItem[] = []
    for (const k of g.items) {
      const item = byKey.get(k)
      if (!item || placed.has(k)) continue
      placed.add(k)
      items.push(item)
    }
    groups.push({ key: g.key, items })
  }
  for (const d of NAV) {
    for (const i of d.items) {
      if (placed.has(i.key)) continue
      let g = groups.find((x) => x.key === d.key)
      if (!g) { g = { key: d.key, items: [] }; groups.push(g) }
      g.items.push(i)
    }
  }
  return groups
}

/**
 * The groups and entries an account can open: its permission, and the org's
 * modules (a screen of a module switched off is hidden). Empty groups are dropped.
 * The sidebar (`menu`) follows its role's layout and leaves out the technical screens; the search and
 * « Page introuvable » keep every screen, in the default order.
 */
export function visibleNav(can: (p: string) => boolean, screenOn: (path: string) => boolean = () => true,
                           opts: { menu?: boolean; layout?: MenuLayout | null } = {}): NavGroup[] {
  return arrangeNav(opts.menu ? opts.layout : null).map((g) => ({ ...g, items: g.items.filter((i) => can(i.perm) && screenOn(i.to) && !(opts.menu && i.technical)) }))
    .filter((g) => g.items.length)
}

/**
 * The sidebar's « Aller à… » (2026-10-22): the entries whose name, or whose group's name, holds every word
 * typed, accents and case aside; the groups left empty are dropped.
 */
export function matchNav(groups: NavGroup[], query: string, label: (i: NavItem) => string, groupLabel: (g: NavGroup) => string): NavGroup[] {
  const words = flat(query).split(/\s+/).filter(Boolean)
  if (!words.length) return groups
  return groups.map((g) => ({ ...g, items: g.items.filter((i) => {
    const text = flat(`${label(i)} ${groupLabel(g)}`)
    return words.every((w) => text.includes(w))
  }) })).filter((g) => g.items.length)
}

/**
 * The first screen outside the agent workstation an account can open, in
 * menu order. The workstation is full screen, without the sidebar: this is
 * the way back to the platform for a supervisor or an admin who opened it.
 */
export function exitFromAgent(can: (p: string) => boolean): string | null {
  return NAV_ITEMS.find((i) => i.to !== '/agent' && can(i.perm))?.to ?? null
}

/**
 * An account whose only job is taking calls: it neither supervises the floor
 * nor reads reports. The test cannot be "has agent.work" (an admin has it
 * too), nor "has another screen" (an agent reads campaigns for its campaign
 * selector). Such an account is locked in the workstation: no sidebar, no
 * other screen, any other address leads back to it.
 */
export function lockedToWorkstation(can: (p: string) => boolean): boolean {
  return can('agent.work') && !can('monitor.view') && !can('report.view')
}

/**
 * Home screen: a locked agent lands on the workstation; everyone else on
 * their first screen; the profile otherwise. An admin, holding every
 * permission, lands on the dashboard.
 */
export function landingFor(can: (p: string) => boolean): string {
  if (lockedToWorkstation(can)) return '/agent'
  return exitFromAgent(can) ?? (can('agent.work') ? '/agent' : '/profile')
}

/** Letters to change, add or remove to go from one word to the other. */
function distance(a: string, b: string): number {
  let row = Array.from({ length: b.length + 1 }, (_, j) => j)
  for (let i = 1; i <= a.length; i++) {
    const next = [i]
    for (let j = 1; j <= b.length; j++) next[j] = Math.min(row[j]! + 1, next[j - 1]! + 1, row[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1))
    row = next
  }
  return row[b.length]!
}

/**
 * The screens an address leading nowhere may have meant (2026-10-12), among those given (the ones
 * the account can open), in menu order: a word of the address that is a word of a screen's address
 * or name (`/system/xyz` → the system's screens), begins one, or is a slip of the keyboard away from
 * one (`/direktion` → Direction, a third of its letters at most). Words under three letters say nothing.
 */
export function nearScreens(path: string, items: readonly NavItem[], label: (i: NavItem) => string, max = 3): NavItem[] {
  let asked = path
  try { asked = decodeURIComponent(path) } catch { /* a malformed escape: read as typed */ }
  const words = flat(asked).split(/[^\p{L}\p{N}]+/u).filter((w) => w.length >= 3)
  if (!words.length) return []
  const close = (w: string, o: string) => o.length >= 3
    && (w === o || o.startsWith(w) || w.startsWith(o) || distance(w, o) <= Math.floor(Math.max(w.length, o.length) / 3))
  return items.filter((i) => {
    const own = flat(`${i.to} ${label(i)}`).split(/[^\p{L}\p{N}]+/u)
    return words.some((w) => own.some((o) => close(w, o)))
  }).slice(0, max)
}

/**
 * The account is kept in the workstation right now. The temporary way out of the tests was removed on
 * 2026-10-19: an agent reaches their profile from the workstation's bar (« Mon profil »), never another screen.
 */
export function workstationLocked(can: (p: string) => boolean): boolean {
  return lockedToWorkstation(can)
}
